export type WorkingDayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
// Office and site are open together: one "office" mode covers both.
export type WorkingDayMode = 'office' | 'closed';
export type HolidayMode = 'closed' | 'custom';
export type ResolvedMode = 'office' | 'after-hours';

export interface WorkingDaySchedule {
  mode: WorkingDayMode;
  from: string; // "HH:mm"
  to: string; // "HH:mm"
}

export interface WhatsappHoliday {
  id: string;
  name: string;
  fromDate: string; // "YYYY-MM-DD"
  toDate: string; // "YYYY-MM-DD"
  mode: HolidayMode;
  from?: string | null;
  to?: string | null;
}

export type BusyNoteWaitMode = 'learn' | 'fixed';
export type AutoReplyMode = 'off' | 'test' | 'live';
export type FlowMode = AutoReplyMode;

/** Flows start off so each can be tested and turned on one by one; opt-out and the footer start on. */
export const DEFAULT_FLOW_ENABLED: Record<string, boolean> = {
  afterHours: false,
  newContact: false,
  busyNote: false,
  brochure: false,
  siteVisit: false,
  callback: false,
  projectExplore: true,
  unknownContact: false,
  existingCustomer: false,
  optOut: true,
  shared: true,
};

export const isFlowEnabled = (settings: Pick<WhatsappAutoReplySettings, 'flowEnabled' | 'flowModes'>, flowKey: string) =>
  settings.flowModes[flowKey]
    ? settings.flowModes[flowKey] !== 'off'
    : settings.flowEnabled[flowKey] ?? DEFAULT_FLOW_ENABLED[flowKey] ?? false;

export const getFlowMode = (settings: WhatsappAutoReplySettings, flowKey: string): FlowMode => {
  const explicit = settings.flowModes[flowKey];
  if (explicit) return explicit;
  return (settings.flowEnabled[flowKey] ?? DEFAULT_FLOW_ENABLED[flowKey] ?? false)
    ? (settings.autoReplyModeBeforePause || settings.autoReplyMode) : 'off';
};

/** Last 10 digits of an Indian mobile, or '' when it is not one. */
export const toPhone10 = (value: string) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
};

export interface WhatsappAutoReplySettings {
  enabled: boolean;
  timezone: string;
  weekly: Record<WorkingDayKey, WorkingDaySchedule>;
  holidays: WhatsappHoliday[];
  /** Legacy shared mode for flows without an explicit mode. */
  autoReplyMode: AutoReplyMode;
  autoReplyModeBeforePause: AutoReplyMode | null;
  /** Per-automation sending mode. Missing keys retain the legacy shared mode. */
  flowModes: Record<string, FlowMode>;
  /** Emergency stop for all automatic WhatsApp messages. */
  automationPaused: boolean;
  /** 10-digit numbers that get auto-replies in test mode. */
  testNumbers: string[];
  /** Per-flow on/off, keyed by flow key (afterHours, busyNote, ...). Saved instantly from the messages tab. */
  flowEnabled: Record<string, boolean>;
  busyNoteWaitMode: BusyNoteWaitMode;
  busyNoteFixedMinutes: number;
  /** Language used when nothing better is known about the customer. */
  replyLanguage: ReplyLanguage;
  /** Reply in Gujarati / Hindi when the customer writes in that script. */
  matchCustomerScript: boolean;
}

export type ReplyLanguage = 'en' | 'gu' | 'hi';

export const BUSY_NOTE_MIN_MINUTES = 5;
export const BUSY_NOTE_MAX_MINUTES = 120;

export const WHATSAPP_SETTINGS_COLLECTION = 'whatsappSettings';
export const WHATSAPP_AUTO_REPLY_DOC = 'autoReply';

export const DAY_KEYS: WorkingDayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_LABELS: Record<WorkingDayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export const DEFAULT_AUTO_REPLY_SETTINGS: WhatsappAutoReplySettings = {
  enabled: false,
  timezone: 'Asia/Kolkata',
  weekly: {
    mon: { mode: 'office', from: '10:00', to: '19:00' },
    tue: { mode: 'office', from: '10:00', to: '19:00' },
    wed: { mode: 'office', from: '10:00', to: '19:00' },
    thu: { mode: 'office', from: '10:00', to: '19:00' },
    fri: { mode: 'office', from: '10:00', to: '19:00' },
    sat: { mode: 'office', from: '10:00', to: '19:00' },
    sun: { mode: 'office', from: '10:00', to: '19:00' },
  },
  holidays: [],
  autoReplyMode: 'off',
  autoReplyModeBeforePause: null,
  flowModes: {},
  automationPaused: false,
  testNumbers: [],
  flowEnabled: { ...DEFAULT_FLOW_ENABLED },
  busyNoteWaitMode: 'learn',
  busyNoteFixedMinutes: 15,
  replyLanguage: 'en',
  matchCustomerScript: true,
};

export const normalizeAutoReplySettings = (raw: any): WhatsappAutoReplySettings => {
  const weekly = { ...DEFAULT_AUTO_REPLY_SETTINGS.weekly };
  DAY_KEYS.forEach((key) => {
    const row = raw?.weekly?.[key];
    if (row) {
      weekly[key] = {
        // Older settings had a separate "site-open" day: it is an open day.
        mode: row.mode === 'closed' ? 'closed' : row.mode === 'office' || row.mode === 'site-open' ? 'office' : weekly[key].mode,
        from: String(row.from || weekly[key].from),
        to: String(row.to || weekly[key].to),
      };
    }
  });
  const d = DEFAULT_AUTO_REPLY_SETTINGS;
  const fixedMinutes = Number(raw?.busyNoteFixedMinutes);
  return {
    enabled: !!raw?.enabled,
    timezone: String(raw?.timezone || d.timezone),
    weekly,
    holidays: Array.isArray(raw?.holidays) ?
      raw.holidays.map((h: any) => (h?.mode === 'site-open' ? { ...h, mode: 'custom' } : h)) : [],
    autoReplyMode: ['off', 'test', 'live'].includes(raw?.autoReplyMode) ? raw.autoReplyMode : d.autoReplyMode,
    autoReplyModeBeforePause: ['off', 'test', 'live'].includes(raw?.autoReplyModeBeforePause) ? raw.autoReplyModeBeforePause : null,
    flowModes: Object.fromEntries(Object.entries(raw?.flowModes && typeof raw.flowModes === 'object' ? raw.flowModes : {}).filter(([, value]) => ['off', 'test', 'live'].includes(String(value)))) as Record<string, FlowMode>,
    automationPaused: raw?.automationPaused === true,
    testNumbers: Array.isArray(raw?.testNumbers) ? raw.testNumbers.map((n: unknown) => toPhone10(String(n))).filter(Boolean) : [],
    flowEnabled: { ...DEFAULT_FLOW_ENABLED, ...(raw?.flowEnabled && typeof raw.flowEnabled === 'object' ? raw.flowEnabled : {}) },
    busyNoteWaitMode: raw?.busyNoteWaitMode === 'fixed' ? 'fixed' : 'learn',
    busyNoteFixedMinutes: Number.isFinite(fixedMinutes) && fixedMinutes > 0 ? fixedMinutes : d.busyNoteFixedMinutes,
    replyLanguage: ['en', 'gu', 'hi'].includes(raw?.replyLanguage) ? raw.replyLanguage : d.replyLanguage,
    matchCustomerScript: typeof raw?.matchCustomerScript === 'boolean' ? raw.matchCustomerScript : d.matchCustomerScript,
  };
};

const toMinutes = (hhmm: string) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

/** Date/time parts of `date` in the given IANA timezone. */
export const zonedParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  return {
    ymd: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
    dayKey: get('weekday').toLowerCase().slice(0, 3) as WorkingDayKey,
  };
};

export interface ResolvedModeResult {
  mode: ResolvedMode;
  reason: string;
}

/** Priority: holiday → weekly day → outside hours = after-hours. */
export const resolveWorkingMode = (
  settings: WhatsappAutoReplySettings,
  date: Date
): ResolvedModeResult => {
  const { ymd, minutes, dayKey } = zonedParts(date, settings.timezone);
  const holiday = settings.holidays.find(
    (h) => h.fromDate && h.toDate && ymd >= h.fromDate && ymd <= h.toDate
  );
  if (holiday) {
    if (holiday.mode === 'closed') {
      return { mode: 'after-hours', reason: `Holiday: ${holiday.name || 'Unnamed'} (closed all day)` };
    }
    const inside = minutes >= toMinutes(holiday.from || '') && minutes < toMinutes(holiday.to || '');
    return inside ?
      {
        mode: 'office',
        reason: `Holiday: ${holiday.name || 'Unnamed'} (open ${holiday.from}–${holiday.to})`,
      } :
      { mode: 'after-hours', reason: `Holiday: ${holiday.name || 'Unnamed'}, outside ${holiday.from}–${holiday.to}` };
  }
  const day = settings.weekly[dayKey];
  if (!day || day.mode === 'closed') {
    return { mode: 'after-hours', reason: `${DAY_LABELS[dayKey] || dayKey} is closed` };
  }
  const inside = minutes >= toMinutes(day.from) && minutes < toMinutes(day.to);
  return inside ?
    { mode: 'office', reason: `${DAY_LABELS[dayKey]} open ${day.from}–${day.to}` } :
    { mode: 'after-hours', reason: `${DAY_LABELS[dayKey]}, outside ${day.from}–${day.to}` };
};

export const validateAutoReplySettings = (settings: WhatsappAutoReplySettings): string[] => {
  const errors: string[] = [];
  DAY_KEYS.forEach((key) => {
    const day = settings.weekly[key];
    if (day.mode === 'closed') return;
    const from = toMinutes(day.from);
    const to = toMinutes(day.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) errors.push(`${DAY_LABELS[key]}: set both times.`);
    else if (to <= from) errors.push(`${DAY_LABELS[key]}: closing time must be after opening time.`);
  });
  settings.holidays.forEach((h, idx) => {
    const label = h.name?.trim() || `Holiday #${idx + 1}`;
    if (!h.name?.trim()) errors.push(`Holiday #${idx + 1}: name required.`);
    if (!h.fromDate || !h.toDate) errors.push(`${label}: set date.`);
    else if (h.toDate < h.fromDate) errors.push(`${label}: end date before start date.`);
    if (h.mode !== 'closed') {
      const from = toMinutes(h.from || '');
      const to = toMinutes(h.to || '');
      if (!Number.isFinite(from) || !Number.isFinite(to)) errors.push(`${label}: set both times.`);
      else if (to <= from) errors.push(`${label}: closing time must be after opening time.`);
    }
  });
  if ((settings.autoReplyMode === 'test' || Object.values(settings.flowModes).includes('test')) && !settings.testNumbers.length) {
    errors.push('Add at least one test number, or switch auto-replies off.');
  }
  if (settings.busyNoteWaitMode === 'fixed') {
    const m = settings.busyNoteFixedMinutes;
    if (!Number.isFinite(m) || m < BUSY_NOTE_MIN_MINUTES || m > BUSY_NOTE_MAX_MINUTES) {
      errors.push(`Busy note wait must be ${BUSY_NOTE_MIN_MINUTES}–${BUSY_NOTE_MAX_MINUTES} minutes.`);
    }
  }
  const dated = settings.holidays.filter((h) => h.fromDate && h.toDate);
  for (let i = 0; i < dated.length; i += 1) {
    for (let j = i + 1; j < dated.length; j += 1) {
      const a = dated[i];
      const b = dated[j];
      if (a.fromDate <= b.toDate && b.fromDate <= a.toDate) {
        errors.push(`Holidays overlap: ${a.name || 'Unnamed'} and ${b.name || 'Unnamed'}.`);
      }
    }
  }
  return errors;
};

/**
 * Reply language for a contact: saved lead preference, else the script of their message
 * (Gujarati / Devanagari) when enabled, else the default.
 */
export const pickReplyLanguage = (
  settings: Pick<WhatsappAutoReplySettings, 'replyLanguage' | 'matchCustomerScript'>,
  inboundText: string,
  leadLanguage?: string | null
): ReplyLanguage => {
  const saved = String(leadLanguage || '').toLowerCase();
  if (saved === 'en' || saved === 'gu' || saved === 'hi') return saved;
  if (settings.matchCustomerScript) {
    if (/[\u0A80-\u0AFF]/.test(inboundText)) return 'gu';
    if (/[\u0900-\u097F]/.test(inboundText)) return 'hi';
  }
  return settings.replyLanguage;
};
